import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getLeadBalancesApi,
  getLeadPartnersApi,
  getRefOrdersApi,
  LeadBalance,
  LeadEarnData,
  LeadOrders,
  LeadOrdersResponse,
} from "./api";
import { State } from "@features/base/types";
import { ReferralDetails } from "@features/admin/users/api";
import { PaginationParameters } from "@request";

export const getLeadBalances = createAsyncThunk<LeadBalance, string>(
  "lead/getLeadBalances",
  async (userId, { rejectWithValue }) => {
    try {
      return await getLeadBalancesApi(userId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getLeadPartners = createAsyncThunk<LeadEarnData, string>(
  "lead/getLeadPartners",
  async (userId, { rejectWithValue }) => {
    try {
      return await getLeadPartnersApi(userId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getRefOrders = createAsyncThunk<
  LeadOrdersResponse,
  { userId: string; data: PaginationParameters }
>("lead/getRefOrders", async ({ userId, data }, { rejectWithValue }) => {
  try {
    return await getRefOrdersApi(userId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export type LeadState = State<{
  balance: LeadBalance | null;
  partners: ReferralDetails[] | null;
  orders: LeadOrders[] | null;
  ordersTotalCount: number,
  balanceLoading: boolean;
  partnersLoading: boolean;
}>;

const initialState: LeadState = {
  balance: null,
  partners: null,
  orders: null,
  ordersTotalCount: 0,
  error: null,
  loading: false,
  balanceLoading: false,
  partnersLoading: false,
};

const leadSlice = createSlice({
  name: "lead",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLeadBalances.pending, (state) => {
        state.balanceLoading = true;
        state.error = null;
      })
      .addCase(getLeadBalances.fulfilled, (state, action) => {
        state.balance = action.payload;
        state.balanceLoading = false;
        state.error = null;
      })
      .addCase(getLeadBalances.rejected, (state, action) => {
        state.balanceLoading = false;
        state.error = action.payload as string | null;
      })
      .addCase(getLeadPartners.pending, (state) => {
        state.partnersLoading = true;
        state.error = null;
      })
      .addCase(getLeadPartners.fulfilled, (state, action) => {
        state.partners = action.payload.referral_details;
        state.partnersLoading = false;
        state.error = null;
      })
      .addCase(getLeadPartners.rejected, (state, action) => {
        state.partnersLoading = false;
        state.error = action.payload as string | null;
      })
      .addCase(getRefOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRefOrders.fulfilled, (state, action) => {
        state.orders = action.payload.orders;
        state.ordersTotalCount = action.payload.count;
        state.loading = false;
        state.error = null;
      })
      .addCase(getRefOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string | null;
      });
  },
});
export default leadSlice.reducer;
